import clsx from "clsx";
import { BACKEND } from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { Spinner } from "ds/ui";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useTranslations } from "translations";
import { setupPdfWorker } from "./pdfWorker";

setupPdfWorker();

export type PdfViewerFile = {
  fileName: string;
  isDeleted?: boolean;
  url: string;
  uploadedAt?: number;
  id: string | number;
};

const useContainerWidth = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const widthRef = useRef<number>(0);
  const isInitialMountRef = useRef(true);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measureWidth = () => {
      const width = element.clientWidth;
      if (width > 0) {
        if (isInitialMountRef.current) {
          widthRef.current = width;
          setContainerWidth(width);
          isInitialMountRef.current = false;
        } else if (Math.abs(width - widthRef.current) > 1) {
          widthRef.current = width;
          setContainerWidth(width);
        }
      }
    };

    measureWidth();
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let frameId = 0;
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && Math.abs(width - widthRef.current) > 1) {
        cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          widthRef.current = width;
          setContainerWidth(width);
        });
      }
    });

    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    containerWidth,
  };
};

export const PdfViewer = ({
  isLoadingPdf,
  selectedFile,
}: {
  selectedFile: PdfViewerFile;
  isLoadingPdf?: boolean;
}) => {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();

  const [numPages, setNumPages] = useState<number>(0);
  const { containerRef, containerWidth } = useContainerWidth();

  const memoizedFile = useMemo(() => {
    const isAiCoreHostedFile =
      aiCoreConfig?.host && selectedFile?.url?.includes(aiCoreConfig?.host);

    if (isAiCoreHostedFile) {
      return {
        httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
        url: selectedFile.url,
      };
    }
    return {
      url: selectedFile.url,
    };
  }, [selectedFile.url, aiCoreConfig?.host]);

  useEffect(() => {
    setNumPages(0);
  }, [selectedFile.url]);

  const handleError = (err: Error) => {
    console.error("PDF load error:", err.message);
  };

  const pageWidth = Math.floor(containerWidth);

  if (!memoizedFile.url) return null;

  return (
    <div
      ref={containerRef}
      className={clsx(
        "flex w-full items-center justify-center overflow-y-auto rounded-[2px] border border-gray-400 shadow-lg",
        {
          "h-full": isLoadingPdf || !numPages,
        },
      )}
      data-testid="pdf-viewer"
      style={{ scrollbarGutter: "stable" }}
    >
      {isLoadingPdf ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner
            color="primary"
            size="lg"
            testId="spinner-loading-pdf-document"
          />
        </div>
      ) : (
        <Document
          className="h-full"
          error={
            <p>{translations.extractDocs.docsCreation.errorPdfLoadingFailed}</p>
          }
          file={memoizedFile}
          loading={
            <Spinner
              color="primary"
              size="lg"
              testId="spinner-loading-pdf-document"
            />
          }
          onLoadError={handleError}
          onLoadSuccess={(info) => setNumPages(info.numPages)}
          onSourceError={handleError}
        >
          {pageWidth > 0 &&
            Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i}
                error={null}
                loading={null}
                pageNumber={i + 1}
                renderAnnotationLayer={false}
                renderForms
                renderTextLayer={false}
                width={pageWidth}
              />
            ))}
        </Document>
      )}
    </div>
  );
};
